import React, { useState, useEffect } from 'react';
import StudentLayout from '../../components/StudentLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Event as EventIcon,
  MonetizationOn as FeeIcon,
  Info as InfoIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';
import axios from 'axios';

const StudentNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading notifications');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      class_update: <ScheduleIcon />,
      schedule_change: <ScheduleIcon />,
      event: <EventIcon />,
      fee_reminder: <FeeIcon />,
      general: <InfoIcon />
    };
    return icons[type] || <NotificationIcon />;
  };

  const getNotificationColor = (type) => {
    const colors = {
      class_update: 'info',
      schedule_change: 'warning',
      event: 'success',
      fee_reminder: 'error',
      general: 'default'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Notifications
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Stay updated with important announcements and updates
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {notifications.length === 0 ? (
          <Card sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
            border: '1px solid',
            borderColor: 'rgba(212, 175, 55, 0.2)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
            },
          }}>
            <CardContent sx={{ pl: 3 }}>
              <Typography variant="body1" sx={{ color: '#8B7355' }} align="center" py={4}>
                No notifications at this time.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
            border: '1px solid',
            borderColor: 'rgba(212, 175, 55, 0.2)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
            },
          }}>
            <List sx={{ pl: 2 }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.3s'
                    }}
                  >
                    <ListItemIcon sx={{ mt: 1 }}>
                      {getNotificationIcon(notification.notification_type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.notification_type.replace('_', ' ')}
                            size="small"
                            color={getNotificationColor(notification.notification_type)}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.primary" paragraph>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notification.created_at).toLocaleString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        )}
      </Box>
    </StudentLayout>
  );
};

export default StudentNotifications;
